import * as React from "react";
import {
   Box,
   Button,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle
} from "@mui/material";
import {FormProvider, useForm} from "react-hook-form";
import {FormInputText} from "../shop/wholesale/form/FormInputText";
import {FormInputSelect, SelectOption} from "../shop/wholesale/form/FormInputSelect";
import {FormInputCheckbox} from "../shop/wholesale/form/FormInputCheckbox";
import {
   EnumOrder2CarrierInputCarrier,
   EnumOrder2CarrierInputDelivery,
   EnumOrder2FulState,
   EnumOrder2OrderState,
   EnumOrder2PayState,
   FlexBrandItemFragment,
   useBrandItemsListByIdsQuery,
   useOrderCreateMutation,
   useProformaOrderAddressesQuery,
   useProformaOrderLogisticsPartnerQuery
} from "../../Queries";
import {SavingUI} from "../../SavingProvider";
import {
   buildProformaAddressBook,
   emptyProformaAddress,
   ProformaAddressFields,
   ProformaAddressSource
} from "./proformaAddress";

type ProformaOrderDialogProps = {
   onCancel: () => void;
   onComplete: () => void;
   ids: string[];
   brandHouseId: string;
   shopId: string;
   shopCurrency: string;
};

type ProformaOrderFormData = ProformaAddressFields & {
   addressSource: ProformaAddressSource;
   carrier: EnumOrder2CarrierInputCarrier;
   delivery: EnumOrder2CarrierInputDelivery;
   proformaInvoiceCommission: boolean;
   proformaInvoicePickPack: boolean;
   proformaInvoiceShipping: boolean;
};

const carrierOptions: SelectOption[] = [
   {id: EnumOrder2CarrierInputCarrier.GLS, name: 'GLS'},
   {id: EnumOrder2CarrierInputCarrier.DHL, name: 'DHL'},
   {id: EnumOrder2CarrierInputCarrier.UPS, name: 'UPS'},
   {id: EnumOrder2CarrierInputCarrier.DAO, name: 'DAO'},
   {id: EnumOrder2CarrierInputCarrier.DPOST, name: 'DPOST'},
   {id: EnumOrder2CarrierInputCarrier.B2C, name: 'B2C'},
   {id: EnumOrder2CarrierInputCarrier.PNO, name: 'PNO'}
];

const deliveryOptions: SelectOption[] = [
   {id: EnumOrder2CarrierInputDelivery.BUSINESS, name: 'Business'},
   {id: EnumOrder2CarrierInputDelivery.HOME, name: 'Home'},
   {id: EnumOrder2CarrierInputDelivery.SHOP, name: 'Shop'},
   {id: EnumOrder2CarrierInputDelivery.CLICKCOLLECT, name: 'Click & Collect'}
];

const addressSourceOptions: SelectOption[] = [
   {id: ProformaAddressSource.Brandhouse, name: 'Brandhouse'},
   {id: ProformaAddressSource.Shop, name: 'Shop'},
   {id: ProformaAddressSource.LogisticsPartner, name: 'Logistics partner'},
   {id: ProformaAddressSource.Custom, name: 'Custom'}
];

/**
 * Creates proforma orders for the selected brand items. The receiver address can be taken from the brandhouse
 * (the default), the shop or the logistics partner, or typed in; a source without an address gives empty fields.
 */
export const ProformaOrderDialog = ({
                                       onCancel,
                                       onComplete,
                                       ids,
                                       brandHouseId,
                                       shopId,
                                       shopCurrency
                                    }: ProformaOrderDialogProps) => {
   const [createOrder] = useOrderCreateMutation();
   const {data, loading: itemsLoading} = useBrandItemsListByIdsQuery({
      variables: {
         ids: ids
      }
   });
   const {data: addressData, loading: addressesLoading} = useProformaOrderAddressesQuery({
      variables: {
         brandHouseId,
         shopId
      }
   });
   const logisticsPartnerId = addressData?.BrandHouse?.logisticsPartnerId;
   const {data: partnerData, loading: partnerLoading} = useProformaOrderLogisticsPartnerQuery({
      variables: {
         logisticsPartnerId
      },
      skip: !logisticsPartnerId
   });
   const loading = itemsLoading || addressesLoading || partnerLoading;

   const rows: FlexBrandItemFragment[] = React.useMemo(() => {
      return data?.BrandItems?.map(item => item?.resolved).filter(Boolean) as FlexBrandItemFragment[] || [];
   }, [data]);

   const addressBook = React.useMemo(() => buildProformaAddressBook(
      addressData?.BrandHouse?.address,
      addressData?.Shop?.address,
      partnerData?.LogisticsPartner
   ), [addressData, partnerData]);

   const form = useForm<ProformaOrderFormData>({
      defaultValues: {
         ...emptyProformaAddress,
         addressSource: ProformaAddressSource.Brandhouse,
         carrier: EnumOrder2CarrierInputCarrier.GLS,
         delivery: EnumOrder2CarrierInputDelivery.BUSINESS,
         proformaInvoiceCommission: false,
         proformaInvoicePickPack: false,
         proformaInvoiceShipping: false
      }
   });

   const {handleSubmit, setValue} = form;

   /** Replaces every address field in the form with the given address. */
   const fillAddress = React.useCallback((address: ProformaAddressFields) => {
      (Object.keys(address) as (keyof ProformaAddressFields)[]).forEach(field => setValue(field, address[field]));
   }, [setValue]);

   // The addresses arrive after the form is created, so the brandhouse address is filled in once they have
   // loaded. After that only the address source dropdown replaces the fields, so edits are never overwritten.
   const prefilled = React.useRef(false);
   React.useEffect(() => {
      if (!loading && !prefilled.current) {
         prefilled.current = true;
         fillAddress(addressBook[ProformaAddressSource.Brandhouse]);
      }
   }, [loading, addressBook, fillAddress]);

   const onSubmit = async (data: ProformaOrderFormData) => {
      await SavingUI.process(async () => {
         const address: ProformaAddressFields = {
            name: data.name,
            company: data.company,
            add1: data.add1,
            add2: data.add2,
            zipCode: data.zipCode,
            city: data.city,
            country: data.country,
            countryCode: data.countryCode,
            mail: data.mail,
            phone: data.phone
         };

         const sortedRows = rows.sort(
            (a, b) => (a.stockLocation || ("Z" + a._id))
               .localeCompare(b.stockLocation || ("Z" + b._id))
         );

         // Base order ID. We'll append an index if we need multiple orders.
         const orderIdBase = "Proforma-" + Date.now().toString();

         // Define maximum lines per order
         const MAX_LINES = 100;

         // Split the sortedRows into chunks of 100
         for (let i = 0; i < sortedRows.length; i += MAX_LINES) {
            const chunk = sortedRows.slice(i, i + MAX_LINES);

            // Generate a unique order ID for each chunk if there are multiple
            const orderIndex = (i / MAX_LINES) + 1;
            const orderId = orderIdBase + (orderIndex > 1 ? `-${orderIndex}` : '');

            const lines = chunk.map(row => ({
               id: {
                  externalId: orderId + "-" + row._id
               },
               externalLineId: orderId + "-" + row._id,
               ean: row.itemEan,
               sku: row.itemSku,
               currency: shopCurrency,
               discountedPrice: row.sellingPrices?.find(price => price?.currency === shopCurrency)?.value || 0,
               salesPrice: row.sellingPrices?.find(price => price?.currency === shopCurrency)?.value || 0,
               quantity: 1,
               discountedPricePreTax: row.sellingPrices?.find(price => price?.currency === shopCurrency)?.value || 0
            }));

            await createOrder({
               variables: {
                  order: {
                     id: {externalId: orderId},
                     billingAddress: address,
                     shopId: shopId,
                     externalId: orderId,
                     orderDate: new Date(),
                     orderName: orderId,
                     orderState: EnumOrder2OrderState.OPEN,
                     carrierInput: {
                        carrier: data.carrier,
                        delivery: data.delivery,
                        express: false
                     },
                     // The receiver's contact details go on the consumer too: the Shipmondo connector takes
                     // the receiver email from there, and GLS refuses to book a shipment without one
                     consumer: {
                        name: address.company || address.name,
                        mail: address.mail,
                        phone: address.phone
                     },
                     currency: shopCurrency,
                     proforma: true,
                     proformaInvoiceCommission: data.proformaInvoiceCommission,
                     proformaInvoicePickPack: data.proformaInvoicePickPack,
                     proformaInvoiceShipping: data.proformaInvoiceShipping,
                     fulState: EnumOrder2FulState.OPEN,
                     payState: EnumOrder2PayState.UNPAID,
                     shippingAddress: address,
                     lines: lines
                  }
               }
            });
         }
      }, 'Proforma order created', 'Creating, please wait');

      onComplete();
   };

   return (
      <Dialog open={true} onClose={onCancel} fullWidth maxWidth="md">
         <DialogTitle>Create Proforma Order</DialogTitle>
         <DialogContent>
            {loading ? (
               <Box sx={{display: 'flex', justifyContent: 'center', padding: '40px'}}>
                  <CircularProgress/>
               </Box>
            ) : (
               <FormProvider {...form}>
                  <form>
                     <Box sx={{marginTop: '16px'}}>
                        <FormInputSelect
                           name="addressSource"
                           label="Address from"
                           options={addressSourceOptions}
                           required
                           onSelectChange={e => fillAddress(addressBook[e.target.value as ProformaAddressSource])}
                        />
                        <FormInputText name="name" label="Name"/>
                        <FormInputText name="company" label="Company"/>
                        <FormInputText name="add1" label="Address 1"/>
                        <FormInputText name="add2" label="Address 2"/>
                        <FormInputText name="zipCode" label="Zip Code"/>
                        <FormInputText name="city" label="City"/>
                        <FormInputText name="country" label="Country"/>
                        <FormInputText name="countryCode" label="Country Code (2 character ISO code)"/>
                        <FormInputText name="mail" label="Email"/>
                        <FormInputText name="phone" label="Phone"/>
                        <FormInputSelect name="carrier" label="Carrier" options={carrierOptions} required/>
                        <FormInputSelect name="delivery" label="Delivery Type" options={deliveryOptions} required/>
                        <Box sx={{marginTop: '16px'}}>
                           <FormInputCheckbox name="proformaInvoiceCommission" label="Add Commission"/>
                           <FormInputCheckbox name="proformaInvoicePickPack" label="Add Pick & Pack"/>
                           <FormInputCheckbox name="proformaInvoiceShipping" label="Add Shipping"/>
                        </Box>
                     </Box>
                  </form>
               </FormProvider>
            )}
         </DialogContent>
         <DialogActions>
            <Button variant="outlined" onClick={onCancel}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={loading || rows.length === 0}>Create Order</Button>
         </DialogActions>
      </Dialog>
   );
};
